const wrap = (value, count) => ((value % count) + count) % count;

/** Resolve an arrow target in card strides, including the short final page. */
export function getGalleryTarget(position, count, perPage, direction) {
  if (count < 2) return 0;
  const pages = Math.ceil(count / perPage);
  const cycle = Math.floor(position / count);
  const offset = wrap(position, count);
  const page = Math.floor(offset / perPage);
  const progress = (offset - page * perPage) / Math.min(perPage, count - page * perPage);
  // The previous (left) arrow returns to the outgoing page even after halfway.
  const delta = direction > 0 ? (progress >= 0.5 ? 2 : 1) : (progress < 0.000001 ? -1 : 0);
  const target = cycle * pages + page + delta;
  return Math.floor(target / pages) * count + wrap(target, pages) * perPage;
}

class HomepageGallery extends HTMLElement {
  #items = [];
  #track;
  #viewport;
  #dots;
  #perPage = 2;
  #stride = 0;
  #position = 0;
  #frame;
  #lastTime = 0;
  #resumeAt = 0;
  #motion = null;
  #visible = false;
  #abort;
  #resize;
  #intersection;
  #reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    this.#track = this.querySelector('.homepage-gallery__grid');
    this.#viewport = this.querySelector('.homepage-gallery__viewport');
    this.#dots = this.querySelector('.homepage-gallery__dots');
    this.#items = [...this.#track.children];
    if (!this.#items.length) return;

    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.addEventListener('click', this.#onClick, { signal });
    this.#viewport.addEventListener('focusin', () => { this.#motion = null; }, { signal });
    this.#viewport.addEventListener('focusout', () => { this.#resumeAt = performance.now() + 3000; }, { signal });
    this.addEventListener('shopify:block:select', (event) => {
      const index = this.#items.findIndex(item => item.contains(event.target));
      if (index >= 0) this.#goTo(Math.floor(index / this.#perPage) * this.#perPage);
    }, { signal });
    this.#resize = new ResizeObserver(() => this.#layout());
    this.#resize.observe(this.#viewport);
    this.#intersection = new IntersectionObserver(([entry]) => { this.#visible = entry.isIntersecting; });
    this.#intersection.observe(this);
    this.#layout();
    this.#lastTime = performance.now();
    this.#frame = requestAnimationFrame(this.#tick);
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.#frame);
    this.#abort?.abort();
    this.#resize?.disconnect();
    this.#intersection?.disconnect();
    this.querySelectorAll('[data-gallery-clone]').forEach(item => item.remove());
    this.#items.forEach(item => { item.inert = false; });
    this.#track?.style.removeProperty('transform');
    this.#motion = null;
    this.#stride = 0;
  }

  #layout() {
    const perPage = Number(getComputedStyle(this).getPropertyValue('--gallery-columns'));
    const gap = parseFloat(getComputedStyle(this.#track).gap);
    const stride = (this.#viewport.clientWidth + gap) / perPage;
    if (stride === this.#stride && perPage === this.#perPage) return;
    this.#stride = stride;
    this.#perPage = perPage;
    this.#motion = null;
    this.#position = Math.floor(wrap(this.#position, this.#items.length) / perPage) * perPage;
    this.querySelectorAll('[data-gallery-clone]').forEach(item => item.remove());

    if (this.#items.length > 1) {
      // One cycle before the originals and enough after them to fill the viewport at the seam.
      const cloneCycle = () => this.#items.map(item => {
        const clone = item.cloneNode(true);
        clone.dataset.galleryClone = '';
        [clone, ...clone.querySelectorAll('[id], [data-shopify-editor-block]')].forEach(node => {
          node.removeAttribute('id');
          node.removeAttribute('data-shopify-editor-block');
        });
        return clone;
      });
      this.#track.prepend(...cloneCycle());
      for (let i = 0; i < Math.ceil(perPage / this.#items.length) + 1; i++) {
        this.#track.append(...cloneCycle());
      }
    }

    const template = this.querySelector('template');
    this.#dots.replaceChildren();
    const pages = Math.ceil(this.#items.length / perPage);
    for (let index = 0; index < pages; index++) {
      const button = template.content.firstElementChild.cloneNode(true);
      button.dataset.galleryPage = index;
      button.setAttribute('aria-label', this.dataset.pageLabel.replace('[index]', index + 1).replace('[length]', pages));
      this.#dots.append(button);
    }
    this.querySelector('.homepage-gallery__controls').hidden = this.#items.length < 2;
    this.#render();
  }

  #onClick = (event) => {
    const button = event.target.closest('button');
    if (!button) return;
    if (button.hasAttribute('data-gallery-step')) {
      this.#goTo(getGalleryTarget(this.#position, this.#items.length, this.#perPage, Number(button.dataset.galleryStep)));
    } else if (button.hasAttribute('data-gallery-page')) {
      const target = Number(button.dataset.galleryPage) * this.#perPage;
      const count = this.#items.length;
      // Dots take the shortest route around the loop.
      const offset = wrap(this.#position, count);
      this.#position = offset;
      this.#goTo([target - count, target, target + count].sort((a, b) => Math.abs(a - offset) - Math.abs(b - offset))[0]);
    }
  };

  #goTo(target) {
    const now = performance.now();
    if (this.#reducedMotion.matches) {
      this.#position = wrap(target, this.#items.length);
      this.#motion = null;
      this.#resumeAt = now + 3000;
      this.#render();
      return;
    }
    this.#motion = { from: this.#position, target, start: now };
  }

  #tick = (now) => {
    const elapsed = Math.min(now - this.#lastTime, 50);
    this.#lastTime = now;
    if (this.#motion) {
      const progress = Math.min((now - this.#motion.start) / 450, 1);
      this.#position = this.#motion.from + (this.#motion.target - this.#motion.from) * (1 - (1 - progress) ** 3);
      if (progress === 1) {
        this.#position = wrap(this.#motion.target, this.#items.length);
        this.#motion = null;
        this.#resumeAt = now + 3000;
      }
      this.#render();
    } else if (this.#items.length > 1 && this.#stride > 0 && this.#visible && !document.hidden &&
      !this.#reducedMotion.matches && !this.#viewport.contains(document.activeElement) && now >= this.#resumeAt) {
      // Constant pixel speed keeps automatic movement continuous across responsive sizes.
      this.#position = wrap(this.#position + elapsed * 0.024 / this.#stride, this.#items.length);
      this.#render();
    }
    this.#frame = requestAnimationFrame(this.#tick);
  };

  #render() {
    const count = this.#items.length;
    const position = wrap(this.#position, count);
    const offset = count > 1 ? count + position : 0;
    this.#track.style.transform = `translate3d(${-offset * this.#stride}px, 0, 0)`;
    const page = Math.floor(position / this.#perPage);
    const length = Math.min(this.#perPage, count - page * this.#perPage);
    const active = (page + (position - page * this.#perPage >= length / 2 ? 1 : 0)) % this.#dots.children.length;
    [...this.#dots.children].forEach((dot, index) => dot.setAttribute('aria-current', String(index === active)));
    [...this.#track.children].forEach((item, index) => {
      const visible = index + 1 > offset + 0.001 && index < offset + this.#perPage - 0.001;
      if (item.inert === visible) item.inert = !visible;
    });
  }
}

if (!customElements.get('homepage-gallery')) {
  customElements.define('homepage-gallery', HomepageGallery);
}
